import { Injectable } from '@angular/core';
import { BaseService } from './base.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError as observableThrowError, of } from 'rxjs';
import { map, publishReplay, refCount, catchError } from 'rxjs/operators';
import { IEventTO } from 'src/app/models/event/transportObjects/eventTO.interface';
import { IBetTO } from 'src/app/models/bet/transportObjects/betTO.interface';
import { IBetTypeGroupTO } from 'src/app/models/betType/transportObjects/betTypeGroupTO.interface';
import { StreamType, EventMappingType, SpecialOfferType } from 'src/app/models/enums';
import { IVideoStreamTO } from 'src/app/models/event/transportObjects/videoStreamTO.interface';
import { ICheckForVideoStreamResponse } from 'src/app/models/event/checkForVideoStreamResponse.intreface';
import { EventVM } from 'src/app/models/event/event';
import { ITOEventsTableConfiguration } from 'src/app/models/betType/temp/TOEventsTableConfiguration.interface';
import { IEventLiveResultInfoTO } from 'src/app/models/event/transportObjects/eventLiveResultInfoTO.interface';
import { IWebEventsDictionaryObjectTO } from 'src/app/models/event/transportObjects/webEventsDictionaryObjectTO.interface';
import { IGetWebBetsResponse } from 'src/app/models/bet/transportObjects/getWebBetsResponse.interface';
import { ISportWithEventsAndSelectionsTO } from 'src/app/models/event/transportObjects/sportWithEventsAndSelectionsTo.interface';
import { CompetitionWithEventsTO } from 'src/app/models/sportHierarchy/transportObjects/competitonWithEventsTO';
import { LoaderService } from '../loader.service';
import { UserActivityService } from '../userActivity.service';
import { NotificationService } from '../notification.service';
import { ConstantValues } from 'src/app/models/util/constantValues';
import { ICompetitionsAndBetTypeGroupsWithEventOrderNoTO } from 'src/app/models/event/transportObjects/competitionsAndBetTypeGroupsWithEventOrderNoTO.interface';
import { IEventsAndGroupsConfigurationsTO } from 'src/app/models/event/transportObjects/eventsAndGroupsConfigurationsTO.intrerface';
import { IWebEventResultsResponseTO } from 'src/app/models/results/transportObjects/webEventResultsResponseTO.interface';
import { DeployParams } from 'src/app/deployParams';
import { StorageService } from '../util/storage.service';
import { IEventV2TO } from 'src/app/models/event/transportObjects/eventV2TO.interface';
import { environment } from 'src/environments/environment';

@Injectable()
export class EventsProviderService extends BaseService {
  constructor(
    private http: HttpClient,
    protected loaderService: LoaderService,
    protected userActivityService: UserActivityService,
    protected notificationService: NotificationService,
    protected storage: StorageService) {
    super(loaderService, userActivityService, notificationService, storage);
  }

  public getEventsForCompetition(
    // response should be events: iEventTo , selections : iselectionTO
    sportId: number,
    competitionId: number,
    dateFrom: Date,
    dateTo: Date,
    isLive: boolean = false,
    eventMappingTypes?: EventMappingType[]): Observable<IEventTO[]> {
    let params = new HttpParams();
    const url: string = this.baseUrlWebClientController + 'loadevents/' +
      sportId.toString() + '/' +
      competitionId.toString() + '/' +
      // later remove replace
      dateFrom.toISOString().replace('Z', '') + '/' +
      dateTo.toISOString().replace('Z', '') + '/' +
      isLive;
    if (!eventMappingTypes || eventMappingTypes.length == 0) {
      eventMappingTypes = [
        EventMappingType.StandardEvent,
        EventMappingType.EventOfDay,
        EventMappingType.SuperBetEvent,
        EventMappingType.Live,
        EventMappingType.Special]
    }
    if (eventMappingTypes) {
      for (const type of eventMappingTypes) {
        params = params.append('eventMappingTypes', type.toString());
      }
    }
    return this.http.get<IEventTO[]>(url, { headers: this.httpHeaders, params }).pipe(
      map((response) => response as IEventTO[]),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        observableThrowError(this.handleError(err));
        return [];
      }),
    );
  }

  getBetsAndGroups(
    sportId: number,
    regionId: number,
    competitionId: number,
    eventId: number): Observable<{ bets: IBetTO[]; groups: IBetTypeGroupTO[] }> {
    const url: string = this.offerController + 'betsAndGroups/' + sportId + '/' + regionId + '/' + competitionId + '/' + eventId;
    const params = new HttpParams();
    return this.http.get<{ bets: IBetTO[]; groups: IBetTypeGroupTO[] }>(url, { headers: this.httpHeaders, params }).pipe(
      map((response) => response as { bets: IBetTO[]; groups: IBetTypeGroupTO[] }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        observableThrowError(this.handleError(err));
        return [];
      }),
    );
  }

  checkForVideoStream(eventIds: number[]): Observable<ICheckForVideoStreamResponse[]> {
    if (eventIds.length === 0 || !environment.checkForLiveStream) {
      return of([]);
    }

    const url: string = this.baseUrlWebClientController + 'checkForVideoStream';
    const params = new HttpParams();

    // let i = 0;

    // remove nulls
    const filteredEventIds = eventIds.filter(eid => eid != null);

    if (filteredEventIds.length === 0) {
      return of([]);
    }

    const body = JSON.stringify({ betradarEventsIds: filteredEventIds });
    // eventIds.forEach(id => {
    //   if (i === 0) {
    //     url += '?betradarEventIds[]=' + id;
    //   } else {
    //     url += '&betradarEventIds[]=' + id;
    //   }
    //   i++;
    // });


    // return this.http.get<ICheckForVideoStreamResponse[]>(url, { headers: this.httpHeaders, params }).pipe(
    //   map(response => response as ICheckForVideoStreamResponse[]),
    //   publishReplay(1),
    //   refCount(),
    //   catchError((err) => {
    //     observableThrowError(this.handleError(err));
    //     return of([]);
    //   })
    // );

    return this.http.post(url, body, { headers: this.httpHeaders, params }).pipe(
      map((response) => response as ICheckForVideoStreamResponse[]),
      publishReplay(1),
      refCount(),
      catchError((err) => of([])),
    );
  }

  getLiveStreamUrl(eventId: number, streamType: StreamType): Observable<IVideoStreamTO> {
    const url: string = this.baseUrlWebClientController + 'videoStream/?betradarEventId=' + eventId + '&streamType=' + streamType.toString();
    const params = new HttpParams();

    return this.http.get<IVideoStreamTO>(url, { headers: this.httpHeaders, params }).pipe(
      map((response) => response as IVideoStreamTO),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        observableThrowError(this.handleError(err));
        return [];
      }),
    );
  }


  getLiveResults(eventIds: number[]): Observable<{ [eventId: number]: IEventLiveResultInfoTO }> {
    if (eventIds.length === 0) {
      return of([]);
    }
    const params = new HttpParams();
    const body = JSON.stringify(eventIds);
    const url: string = this.offerController + 'GetLiveResults';
    return this.http.post(url, body, { headers: this.httpHeaders, params }).pipe(
      map((response) => response as { [eventId: number]: IEventLiveResultInfoTO }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        observableThrowError(this.handleError(err));
        return [];
      }),
    );
  }


  // ovde
  getEventsAndGroupsForFilterWithFirstGroupBets(
    dateFrom: Date,
    dateTo: Date,
    isLive: boolean = false,
    sportId?: number,
    regionId?: number,
    competitionId?: number,
    eventMappingTypes?: EventMappingType[],
    specialOfferType?: SpecialOfferType): Observable<IEventsAndGroupsConfigurationsTO> {
    const url: string = this.offerController + 'getwebevents';
    let params = new HttpParams();
    if (sportId) {
      params = params.set('sportId', sportId.toString());
    }
    if (regionId) {
      params = params.set('regionId', regionId.toString());
    }
    if (competitionId) {
      params = params.set('competitionId', competitionId.toString());
    }

    params = params.set('isLive', isLive.toString().replace('Z', ''));
    params = params.set('dateFrom', dateFrom.toISOString().replace('Z', ''));
    params = params.set('dateTo', dateTo.toISOString().replace('Z', ''));

    if (specialOfferType) {
      params = params.set('specialOfferId', specialOfferType.toString());
    }
    if (!eventMappingTypes || eventMappingTypes.length == 0) {
      eventMappingTypes = [
        EventMappingType.StandardEvent,
        EventMappingType.EventOfDay,
        EventMappingType.SuperBetEvent,
        EventMappingType.Live,
        EventMappingType.Special]
    }
    if (eventMappingTypes) {
      for (const type of eventMappingTypes) {
        params = params.append('eventMappingTypes', type.toString());
      }
    }

    // later remove replace
    return this.http.get<any>(url, { headers: this.httpHeaders, params }).pipe(
      map((response) => {
        const a = 0;
        return response as IEventsAndGroupsConfigurationsTO;
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        observableThrowError(this.handleError(err));
        return [];
      }),
    );
  }

  getBetsForGroup(sportId: number, regionId: number, competitionId: number, betTypeGroupId: number, eventIds: number[]): Observable<IGetWebBetsResponse[]> {
    const params = new HttpParams();
    if (!eventIds || eventIds.length < 1) {
      return of([]);
    }
    const request = {
      sportId,
      regionId,
      competitionId,
      betTypeGroupId,
      eventIds
    };
    const body = JSON.stringify(request);
    const url: string = this.offerController + 'getWebBets';
    return this.http.post(url, body, { headers: this.httpHeaders, params }).pipe(
      map((response) => response as IGetWebBetsResponse[]),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        observableThrowError(this.handleError(err));
        return [];
      }),
    );
  }

  // mobile
  getSpecialOfferEventsMobile(pageId: number, specialOfferId: SpecialOfferType, dateFrom: Date, dateTo: Date): Observable<{ sportWithEventsList: ISportWithEventsAndSelectionsTO[] }> {
    const params = new HttpParams()
      .set('pageId', pageId.toString())
      .set('specialOfferId', specialOfferId.toString())
      .set('dateFrom', dateFrom.toISOString().replace('Z', ''))
      .set('dateTo', dateTo.toISOString().replace('Z', ''));
    const url: string = this.baseUrlMobileClient + 'SpecialOfferEvents';
    return this.http.get<ISportWithEventsAndSelectionsTO[]>(url, { headers: this.httpHeaders, params }).pipe(
      map((response) => response as ISportWithEventsAndSelectionsTO[]),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        observableThrowError(this.handleError(err));
        return [];
      }),
    );
  }

  // mobile cro
  getMostPopularEventsMobile(sportId: number, eventsNumber: number, eventMappingTypes?: EventMappingType[]): Observable<{ sportWithEvents: ISportWithEventsAndSelectionsTO }> {
    const request = {
      betProductId: ConstantValues.BET_PRODUCT_MOBILE_ID,
      isLive: false,
      pageId: ConstantValues.PAGE_ID_PM,
      sportId,
      topN: eventsNumber,
      eventMappingTypes: eventMappingTypes ? eventMappingTypes : []
    };
    const body = JSON.stringify(request);
    const params = new HttpParams();
    // if (eventMappingTypes) {
    //   for (const type of eventMappingTypes) {
    //     params = params.append('eventMappingTypes', type.toString());
    //   }
    // }
    const url: string = this.baseUrlMobileClient + 'MostPopularEventsForSport';
    return this.http.post(url, body, { headers: this.httpHeaders, params }).pipe(
      map((response) => response as ISportWithEventsAndSelectionsTO),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        observableThrowError(this.handleError(err));
        return [];
      }),
    );
  }

  getEventsForSearch(searchValue: string, isLive: boolean): Observable<IEventV2TO[]> {
    const url: string = this.offerController + (environment.useSportBookOffer ? 'SearchEventsWeb/' : 'SearchEvents/') + searchValue + '/' + isLive;
    // let headers = new Headers({ 'Content-Type': 'application/json', 'Language': this.languageCulture, 'OfficeId': this._officeId });
    // let options = new RequestOptions({ headers: headers });
    return this.http.get<IEventV2TO[]>(url, { headers: this.httpHeaders }).pipe(
      map(response => response as IEventV2TO[]),
      catchError(err => {
        observableThrowError(this.handleError(err));
        return [];
      })
    );
  }

  getEventsStartingSoon(isLive: boolean, sportId: number, pageNumber: number, eventMappingTypes?: EventMappingType[]): Observable<ICompetitionsAndBetTypeGroupsWithEventOrderNoTO> {
    const dateTo = new Date();
    let params = new HttpParams();
    if (!eventMappingTypes || eventMappingTypes.length == 0) {
      eventMappingTypes = [
        EventMappingType.StandardEvent,
        EventMappingType.EventOfDay,
        EventMappingType.SuperBetEvent,
        EventMappingType.Live,
        EventMappingType.Special]
    }
    if (eventMappingTypes) {
      for (const type of eventMappingTypes) {
        params = params.append('eventMappingTypes', type.toString());
      }
    }

    dateTo.setSeconds(dateTo.getSeconds() + 20);
    dateTo.setHours(dateTo.getHours() + ConstantValues.HOURS_TO_SPORTS_STARTING_SOON);
    const url: string = this.offerController + 'competitionsWithEventsStartingSoonForSportV2/'
      + ConstantValues.NUMBER_OF_INITIAL_EVENTS_PM
      + '/' + (pageNumber * ConstantValues.NUMBER_OF_INITIAL_EVENTS_PM - ConstantValues.NUMBER_OF_INITIAL_EVENTS_PM)
      + '/' + isLive
      + '/' + dateTo.toISOString().replace('Z', '')
      + '/' + sportId;

    return this.http.get<ICompetitionsAndBetTypeGroupsWithEventOrderNoTO>(url, { headers: this.httpHeaders, params }).pipe(
      map(response => response as ICompetitionsAndBetTypeGroupsWithEventOrderNoTO),
      catchError(err => {
        observableThrowError(this.handleError(err));
        return [];
      })
    );
  }

  getEventsWithCompetitionsForSport(isLive: boolean, sportId: number, pageNumber: number, dateFrom: Date, dateTo: Date, eventMappingTypes: EventMappingType[]): Observable<ICompetitionsAndBetTypeGroupsWithEventOrderNoTO> {
    const url: string = this.offerController + 'competitionsWithEventsForSport/';
    const params = new HttpParams();

    const request = {
      sportId,
      topN: ConstantValues.NUMBER_OF_INITIAL_EVENTS_PM,
      skipN: pageNumber * ConstantValues.NUMBER_OF_INITIAL_EVENTS_PM - ConstantValues.NUMBER_OF_INITIAL_EVENTS_PM,
      isLive,
      dateFrom: dateFrom.toISOString().replace('Z', ''),
      dateTo: dateTo.toISOString().replace('Z', ''),
      eventMappingTypes
    };

    const body = JSON.stringify(request);
    return this.http.post(url, body, { headers: this.httpHeaders, params }).pipe(
      map(response => response as ICompetitionsAndBetTypeGroupsWithEventOrderNoTO),
      catchError(err => {
        observableThrowError(this.handleError(err));
        return [];
      })
    );
  }

  getMostPopularEvents(isLive: boolean, sportId: number, eventMappingTypes?: EventMappingType[]): Observable<ICompetitionsAndBetTypeGroupsWithEventOrderNoTO> {
    const url: string = this.offerController + 'mostPopularEventsV2/'
      + ConstantValues.NUMBER_OF_INITIAL_EVENTS_PM
      + '/' + isLive
      + '/' + sportId;

    let params = new HttpParams();
    if (!eventMappingTypes || eventMappingTypes.length == 0) {
      eventMappingTypes = [
        EventMappingType.StandardEvent,
        EventMappingType.EventOfDay,
        EventMappingType.SuperBetEvent,
        EventMappingType.Live,
        EventMappingType.Special]
    }
    if (eventMappingTypes) {
      for (const type of eventMappingTypes) {
        params = params.append('eventMappingTypes', type.toString());
      }
    }

    return this.http.get<ICompetitionsAndBetTypeGroupsWithEventOrderNoTO>(url, { headers: this.httpHeaders, params }).pipe(
      map(response => response as ICompetitionsAndBetTypeGroupsWithEventOrderNoTO),
      catchError(err => {
        observableThrowError(this.handleError(err));
        return [];
      })
    );
  }

  // for result page
  getEventResults(
    dateFrom: Date,
    dateTo: Date,
    sportId: number,
    regionId?: number,
    competitionId?: number): Observable<IWebEventResultsResponseTO> {
    const url: string = this.offerController + 'eventResults';
    let params = new HttpParams();
    params = params.set('sportId', sportId.toString());

    if (regionId) {
      params = params.set('regionId', regionId.toString());
    }
    if (competitionId) {
      params = params.set('competitionId', competitionId.toString());
    }

    params = params.set('dateFrom', dateFrom.toISOString().replace('Z', ''));
    params = params.set('dateTo', dateTo.toISOString().replace('Z', ''));

    // later remove replace
    return this.http.get<any>(url, { headers: this.httpHeaders, params }).pipe(
      map((response) => response as IWebEventResultsResponseTO),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        observableThrowError(this.handleError(err));
        return [];
      }),
    );
  }

  getResultedBetsAndGroups(
    sportId: number,
    regionId: number,
    competitionId: number,
    eventId: number): Observable<{ bets: IBetTO[]; groups: IBetTypeGroupTO[] }> {
    const url: string = this.offerController + 'resultedBetsAndGroups/' + sportId + '/' + regionId + '/' + competitionId + '/' + eventId;
    const params = new HttpParams();
    return this.http.get<{ bets: IBetTO[]; groups: IBetTypeGroupTO[] }>(url, { headers: this.httpHeaders, params }).pipe(
      map((response) => response as { bets: IBetTO[]; groups: IBetTypeGroupTO[] }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        observableThrowError(this.handleError(err));
        return [];
      }),
    );
  }

  getEventsByCodeOrNameSearch(searchValue: string): Observable<IEventTO[]> {
    const url: string = this.sportBookOfferController + 'GetEventsByCodeOrName/' + searchValue;
    return this.http.get<IEventTO[]>(url, { headers: this.httpHeaders }).pipe(
      map(response => response as IEventTO[]),
      catchError(err => {
        observableThrowError(this.handleError(err, false));
        return [];
      })
    );
  }

  getAllLiveEvents(): Observable<IEventTO[]> {
    const url: string = this.sportBookOfferController + 'GetAllLiveEvents';
    return this.http.get<IEventTO[]>(url, { headers: this.httpHeaders }).pipe(
      map(response => response as IEventTO[]),
      catchError(err => {
        observableThrowError(this.handleError(err, false));
        return [];
      })
    );
  }

  getBetsForEvent(event: EventVM): Observable<IBetTO[]> {
    const params = new HttpParams();
    const body = JSON.stringify({
      sportId: event.sportId,
      regionId: event.regionId,
      competitionId: event.competitionId,
      eventId: event.id
    });
    const url: string = this.sportBookOfferController + 'GetBetsForEventId';
    return this.http.post(url, body, { headers: this.httpHeaders, params }).pipe(
      map((response) => response as IBetTO[]),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        observableThrowError(this.handleError(err, false));
        return [];
      }),
    );
  }
}

